import { useState, useEffect } from 'react';
import { adminAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Save } from 'lucide-react';

const seoFields = [
  { key: 'seo_title', label: 'SEO Title', placeholder: 'Mietwagen auf Sylt | GB Sylt' },
  { key: 'meta_description', label: 'Meta Description', placeholder: 'Mietwagen auf Sylt bequem reservieren...' },
  { key: 'seo_keywords', label: 'Keywords (optional)', placeholder: 'Mietwagen Sylt, Auto mieten...' },
  { key: 'og_title', label: 'Open Graph Title', placeholder: 'Mietwagen auf Sylt' },
  { key: 'og_description', label: 'Open Graph Description', placeholder: '' },
  { key: 'og_image', label: 'Open Graph Image URL', placeholder: 'https://...' },
  { key: 'canonical_url', label: 'Canonical URL', placeholder: 'https://mietwagen.gb-sylt.de' },
  { key: 'robots', label: 'Robots', placeholder: 'index, follow' },
];

export default function SEOEditor() {
  const [seo, setSeo] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminAPI.getSEO().then(res => setSeo(res.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminAPI.updateSEO(seo);
      toast.success('SEO-Einstellungen gespeichert');
    } catch { toast.error('Fehler beim Speichern'); }
    finally { setSaving(false); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0E518D]" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="seo-editor">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">SEO-Einstellungen</h1>
          <p className="text-sm text-slate-500 font-body mt-1">Meta-Tags und Open Graph Daten verwalten</p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#0E518D] hover:bg-[#0B4171] text-white rounded-sm font-body"
          data-testid="seo-save-button"
        >
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Speichern...' : 'Speichern'}
        </Button>
      </div>

      <Card className="border-0 shadow-sm rounded-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-body font-semibold text-slate-700">Suchmaschinen-Optimierung</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {seoFields.map(field => (
            <div key={field.key}>
              <Label className="font-body text-sm">{field.label}</Label>
              <Input
                value={seo[field.key] || ''}
                onChange={(e) => setSeo(prev => ({ ...prev, [field.key]: e.target.value }))}
                className="mt-1 rounded-sm font-body"
                placeholder={field.placeholder}
                data-testid={`seo-field-${field.key}`}
              />
              {field.key === 'og_image' && (
                <p className="text-xs text-slate-400 font-body mt-1">Empfohlen: 1200 x 630 px. Upload unter Medien mit Slug "og_image".</p>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm rounded-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-body font-semibold text-slate-700">Vorschau</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-slate-50 p-4 rounded-sm border border-slate-100">
            <p className="text-[#1a0dab] text-base font-body font-medium truncate">
              {seo.seo_title || 'Seitentitel'}
            </p>
            <p className="text-green-700 text-xs font-body mt-0.5">
              {seo.canonical_url || 'https://mietwagen.gb-sylt.de'}
            </p>
            <p className="text-slate-600 text-xs font-body mt-1 line-clamp-2">
              {seo.meta_description || 'Beschreibung...'}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
